/* The second door: a colleague joining an organisation that already exists.

   Distinct from the access-request queue on purpose. A stranger is admitted by an
   operator; a teammate is vouched for by a customer, and lands inside a configured
   organisation. That is why this route never reaches the setup wizard -- by the time it
   redirects, the account has a membership, and `Gate` sends anyone with one to the
   dashboard.

   The token is redeemed on arrival and stripped from the address bar immediately: an
   invitation left in a URL is a way into the organisation until it is spent. */

import { useEffect, useRef, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";

import { api } from "@/api";
import { AccessPage, QuietLink } from "@/pages/access";
import { useSession } from "@/session";

const WEBSITE_LOGIN = "https://codenerve.ai/login";

export function AcceptInvite() {
  const [params] = useSearchParams();
  const [error, setError] = useState("");
  const [joined, setJoined] = useState(null);
  const navigate = useNavigate();
  const { status, refresh } = useSession();
  const started = useRef(false);

  useEffect(() => {
    /* Once. In development the effect runs twice on mount, and the second call would
       redeem a token the first one has already spent. */
    if (started.current) return;
    started.current = true;

    const token = params.get("token");
    if (!token) {
      setError("That invitation link is missing its token.");
      return;
    }
    let cancelled = false;
    api
      .post("/invitations/accept", { token })
      .then(async (result) => {
        if (cancelled) return;
        if (result.status === "password_required") {
          window.location.replace(`${WEBSITE_LOGIN}?redirect=${encodeURIComponent(window.location.origin)}`);
          return;
        }
        /* The account exists. Signed in already, this is now one of their organisations;
           signed out, they have somewhere to sign in to. */
        if (status === "in") {
          await refresh();
          navigate("/analytics", { replace: true });
          return;
        }
        setJoined(result);
      })
      .catch((failure) => !cancelled && setError(failure.message));
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  if (error) {
    return (
      <AccessPage title="Invitation" lead="This link could not be used.">
        <div style={{ display: "grid", gap: "14px", justifyItems: "center", textAlign: "center" }}>
          <p style={{ font: "var(--text-body-14)", color: "var(--text-muted)", maxWidth: "34ch" }}>
            {error} Invitations work once and expire after fourteen days. Ask whoever invited
            you to send a new one.
          </p>
          <QuietLink onClick={() => window.location.replace(`${WEBSITE_LOGIN}?redirect=${encodeURIComponent(window.location.origin)}`)}>Go to sign in</QuietLink>
        </div>
      </AccessPage>
    );
  }

  if (joined) {
    return (
      <AccessPage
        title={`You have joined ${joined.organization.name}`}
        lead="Sign in to get started."
      >
        <div style={{ display: "grid", gap: "14px", justifyItems: "center", textAlign: "center" }}>
          <p style={{ font: "var(--text-body-14)", color: "var(--text-muted)", maxWidth: "34ch" }}>
            {joined.email} is now a member of {joined.organization.name}.
          </p>
          <a href={`${WEBSITE_LOGIN}?redirect=${encodeURIComponent(window.location.origin)}`} style={{ font: "var(--text-caption)", color: "var(--text-primary)" }}>
            Sign in
          </a>
        </div>
      </AccessPage>
    );
  }

  return (
    <AccessPage title="Invitation" lead="Checking your invitation…">
      <p
        style={{
          font: "var(--text-body-14)",
          color: "var(--text-muted)",
          textAlign: "center",
          margin: 0,
        }}
      >
        One moment.
      </p>
    </AccessPage>
  );
}
